<script setup lang="ts">
import type { User } from "@/models"
import type { IUserForm } from "@/types"
import { toTypedSchema } from "@vee-validate/zod"
import { useUrlSearchParams } from "@vueuse/core"
import { useForm } from "vee-validate"
import { onMounted, ref, watchEffect } from "vue"
import { z } from "zod"
import { useUpdateUser } from "@/api"
import { Button, Form, FormSection, Input } from "@/components/ui"
import { useToast } from "@/components/ui/ToastReka/useToast"
import { useTranslation } from "@/composables"
import { ValidationException } from "@/http"
import { useAuthStore } from "@/stores/authStore"
import { passwordValidation } from "@/utils"

const emit = defineEmits<{ userLoaded: [] }>()

const urlParams = useUrlSearchParams<{ email?: string, token?: string }>()
const validationErrors = ref<{ [key: string]: string[] } | null>(null)
const { t } = useTranslation()
const toast = useToast()
const authStore = useAuthStore()

const { mutate: updateMutate, isPending } = useUpdateUser()

const validationSchema = toTypedSchema(
  z
    .object({
      name: z.string().min(1, t("profile.provide_name")),
      email: z.string().email(t("profile.invalid_email")),
      current_password: z.string().optional(),
      password: passwordValidation({ optional: true }),
      password_confirm: z.string().optional(),
    })
    .refine(fields => fields.password === fields.password_confirm, {
      message: t("profile.password_mismatch"),
      path: ["password_confirm"],
    })
    .refine((fields) => {
      if (!fields.password)
        return true

      return fields.current_password
    }, {
      message: t("profile.provide_current_password"),
      path: ["current_password"],
    }),
)

const { handleSubmit, setFieldValue, resetForm } = useForm({
  validationSchema,
  initialValues: {
    name: "",
    email: "",
    current_password: "",
    password: "",
    password_confirm: "",
  },
})

function initialiseForm(existingUser: User) {
  if (existingUser.name)
    setFieldValue("name", existingUser.name)
  if (existingUser.email)
    setFieldValue("email", existingUser.email)
}

onMounted(() => {
  if (urlParams.email)
    setFieldValue("email", urlParams.email)
})

const onSubmit = handleSubmit((values) => {
  validationErrors.value = null

  const userFields: IUserForm = {
    name: values.name,
    email: values.email,
    current_password: values.current_password,
    new_password: values.password,
    confirm_new_password: values.password_confirm,
  }

  updateMutate(
    { user: userFields },
    {
      onSuccess() {
        resetForm({ values: { ...values, password: "", current_password: "", password_confirm: "" } })
        toast.success({ title: t("success"), description: t("profile_page.success_content") })
      },
      onError(error) {
        if (error instanceof ValidationException) {
          validationErrors.value = error.validationErrors
        }

        toast.error({ title: t("profile_page.error_title"), description: t("profile_page.error_content") })
      },
    },
  )
})

watchEffect(() => {
  if (authStore.isAuthenticated && authStore.user) {
    emit("userLoaded")
    initialiseForm(authStore.user)
  }
})
</script>

<template>
  <Form :busy="isPending" gap="lg" data-test="profile-form" @submit="onSubmit">
    <FormSection
      icon="User"
      :title="t('profile.your_details')"
      :description="t('profile.your_details_description')"
    >
      <Input name="name" :label="t('profile.name_label')" :error="validationErrors?.name?.[0]" data-test="profile-name-input" />
      <Input
        name="email" type="email" :label="t('profile.email_label')" :disabled="authStore.isAuthenticated"
        :hint="t('profile.email_disabled_hint')" :error="validationErrors?.email?.[0]" data-test="profile-email-input"
      />
    </FormSection>

    <FormSection
      icon="Lock"
      :title="t('profile.password_title')"
      :description="t('profile.password_description')"
    >
      <Input
        name="current_password" type="password" :label="t('profile.current_password_label')"
        :hint="t('profile.current_password_hint')" :error="validationErrors?.current_password?.[0]"
        data-test="profile-current-password-input"
      />
      <Input
        name="password" type="password" :label="t('profile.new_password_label')"
        :hint="t('profile.password_hint')" :error="validationErrors?.new_password?.[0]"
        data-test="profile-new-password-input"
      />
      <Input
        name="password_confirm" type="password" :label="t('profile.confirm_password_label')"
        :error="validationErrors?.confirm_new_password?.[0]" data-test="profile-confirm-password-input"
      />
    </FormSection>

    <template #actions>
      <div class="flex justify-end">
        <Button size="lg" :suffix-icon="isPending ? 'Loading' : undefined" data-test="profile-submit-button">
          {{ t("submit") }}
        </Button>
      </div>
    </template>
  </Form>
</template>
